import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getAiVisCssVars } from './aivisTheme';
import './ai-vis.css';
import './Login.css';

const SLIDES = [
  {
    id: 'visibility',
    kicker: 'Measure',
    title: 'AI Visibility',
    body: 'See how often ChatGPT and Google AI Overviews name your brand, and how you compare with the competitors you track.',
    image: '/aivis-login-visibility.jpg',
  },
  {
    id: 'prompts',
    kicker: 'Monitor',
    title: 'Prompt tracking',
    body: 'Keep the prompts that matter, then open any one to see how your mention rate moves over time.',
    image: '/aivis-login-prompts.jpg',
  },
  {
    id: 'citations',
    kicker: 'Explain',
    title: 'Citation intelligence',
    body: 'Find the domains and URLs those answers cite, so you can see which sources shape your visibility.',
    image: '/aivis-login-citations.jpg',
  },
];

const ROTATION_MS = 7000;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => (
    typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ));

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

export default function AiVisLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/ai-vis/home';
  const invited = location.state?.invited;
  const reducedMotion = usePrefersReducedMotion();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [deckPaused, setDeckPaused] = useState(false);

  const slide = SLIDES[slideIndex];

  useEffect(() => {
    if (deckPaused || reducedMotion) return undefined;
    const timer = window.setInterval(() => {
      setSlideIndex((current) => (current + 1) % SLIDES.length);
    }, ROTATION_MS);
    return () => window.clearInterval(timer);
  }, [deckPaused, reducedMotion, slideIndex]);

  function showSlide(nextIndex) {
    setSlideIndex((nextIndex + SLIDES.length) % SLIDES.length);
  }

  function onDeckKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showSlide(slideIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showSlide(slideIndex - 1);
    }
  }

  async function onSubmit(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const data = err.response?.data;
      setError(data?.detail || data?.non_field_errors?.[0] || 'Sign-in failed. Check your email and password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="aivis-auth-page aivis-login-page" style={getAiVisCssVars()}>
      <div className="aivis-login-shell">
        <section className="aivis-login-form-col">
          <div className="aivis-auth-card">
            <img className="aivis-login-mark" src="/180marketing-logo.png" alt="180Marketing" />
            <p className="aivis-login-product">AI-Vis</p>
            <h1>Welcome back</h1>
            <p>Sign in with your invited account.</p>
            {invited && <p className="aivis-positive">Account activated. Sign in below.</p>}
            {error && <div className="aivis-auth-error" role="alert">{error}</div>}
            <form onSubmit={onSubmit}>
              <div className="aivis-field">
                <label htmlFor="aivis-login-email">Email</label>
                <input
                  id="aivis-login-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
              <div className="aivis-field">
                <div className="aivis-field-label-row">
                  <label htmlFor="aivis-login-password">Password</label>
                  <Link className="aivis-login-forgot" to="/ai-vis/forgot-password">
                    Forgot password?
                  </Link>
                </div>
                <div className="aivis-login-password">
                  <input
                    id="aivis-login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="aivis-login-reveal"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    {showPassword ? (
                      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <button type="submit" className="aivis-btn-primary" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
            <div className="aivis-auth-link">
              <Link to="/">Back to portal</Link>
            </div>
          </div>
        </section>

        <aside
          className="aivis-login-feature"
          aria-roledescription="carousel"
          aria-label="What AI-Vis tracks"
          onMouseEnter={() => setDeckPaused(true)}
          onMouseLeave={() => setDeckPaused(false)}
          onFocusCapture={() => setDeckPaused(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setDeckPaused(false);
          }}
          onKeyDown={onDeckKeyDown}
        >
          <div className="aivis-login-stage">
            <img
              key={slide.id}
              className="aivis-login-art"
              src={slide.image}
              alt=""
              width="864"
              height="1152"
            />
            <div className="aivis-login-caption">
              <p className="aivis-login-kicker">{slide.kicker}</p>
              <h2 id={`aivis-login-slide-${slide.id}`}>{slide.title}</h2>
              <p>{slide.body}</p>
              <div className="aivis-login-controls">
                <button
                  type="button"
                  className="aivis-login-nav"
                  aria-label="Previous feature"
                  onClick={() => showSlide(slideIndex - 1)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <div className="aivis-login-dots" role="group" aria-label="Feature slides">
                  {SLIDES.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      className={index === slideIndex ? 'aivis-login-dot aivis-login-dot--active' : 'aivis-login-dot'}
                      aria-label={`Show ${item.title}`}
                      aria-current={index === slideIndex ? 'true' : undefined}
                      onClick={() => showSlide(index)}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="aivis-login-nav"
                  aria-label="Next feature"
                  onClick={() => showSlide(slideIndex + 1)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
                <span className="aivis-login-sr">{`${slideIndex + 1} of ${SLIDES.length}`}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
